import { useState, useEffect, useRef } from 'react';
import { Play, ArrowLeft, Plus, X, Search, Grid, Square, Video, LayoutDashboard } from 'lucide-react';
import baseapi from '../services/baseapi';
import { showError, showSuccess } from '../utils/toast';
import { jwt_decode } from '../utils/jwt_decode';

interface CameraType {
    id: string;
    name: string;
    status: string;
    url: string;
    client_id?: string;
    agent_id?: string;
}

const VideoPlayer = ({ camera, onStateChange, isSinglePlayer = false }: { camera: CameraType; onStateChange?: (state: 'connecting' | 'live' | 'offline') => void; isSinglePlayer?: boolean }) => {
    const videoRef = useRef<HTMLVideoElement>(null);
    const [error, setError] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const video = videoRef.current;
        if (!video) return;

        let pc: RTCPeerConnection | null = null;
        let hls: any = null;
        let retryTimer: any = null;
        let retries = 0;
        const MAX_RETRIES = 6;

        let streamId = '';
        let readToken = '';
        let hlsUrl = '';
        let isUsingHlsFallback = false;
        let isMounted = true;

        const startHls = (url: string, token: string) => {
            if (!isMounted) return;
            const Hls = (window as any).Hls;
            if (!Hls) {
                setError("HLS library not loaded");
                setIsLoading(false);
                onStateChange?.('offline');
                return;
            }

            if (hls) {
                hls.destroy();
            }

            hls = new Hls({
                debug: false,
                manifestLoadingTimeOut: 65000,
                manifestLoadingMaxRetry: 0,
                xhrSetup: function (xhr: any, url: string) {
                    if (token && !url.includes('token=')) {
                        xhr.open('GET', url + (url.includes('?') ? '&' : '?') + 'token=' + token, true);
                    }
                },
            });

            hls.on(Hls.Events.ERROR, (_: any, d: any) => {
                if (!isMounted) return;
                console.warn(`[hls] ${d.type}/${d.details} fatal=${d.fatal}`);
                if (d.fatal) {
                    hls.destroy();
                    hls = null;
                    if (retries < 8) {
                        retries++;
                        const doRefresh = (retries % 2 === 0);
                        console.log(`[hls] Stream not ready - retrying in 3s... (${retries}/8)`);
                        setIsLoading(true);
                        onStateChange?.('connecting');

                        if (doRefresh) {
                            refreshStream().then((freshData) => {
                                if (!isMounted) return;
                                if (freshData) {
                                    hlsUrl = freshData.hls_url;
                                    readToken = freshData.read_token || freshData.hls_url.split('token=')[1] || '';
                                }
                                retryTimer = setTimeout(() => {
                                    if (isMounted) startHls(hlsUrl, readToken);
                                }, 3000);
                            });
                        } else {
                            retryTimer = setTimeout(() => {
                                if (isMounted) startHls(hlsUrl, readToken);
                            }, 3000);
                        }
                    } else {
                        setError("Camera fallback stream unavailable");
                        setIsLoading(false);
                        onStateChange?.('offline');
                    }
                }
            });

            hls.on(Hls.Events.MANIFEST_PARSED, () => {
                if (!isMounted) return;
                setError(null);
                setIsLoading(false);
                onStateChange?.('live');
                video.play().catch(e => console.warn('autoplay: ' + e));
            });

            hls.loadSource(url);
            hls.attachMedia(video);
        };

        const refreshStream = async () => {
            if (!isMounted) return null;
            try {
                const response = await baseapi.post('camera/stream-access', {
                    asset_id: camera.id,
                    client_id: camera.client_id,
                    agent_id: camera.agent_id,
                });
                if (isMounted && response.data.status) {
                    return response.data.data;
                }
            } catch (err) {
                console.error("Failed to refresh stream", err);
            }
            return null;
        };

        const startHlsFallback = () => {
            if (!isMounted) return;
            console.log("[webrtc] Falling back to HLS stream...");
            setIsLoading(true);
            onStateChange?.('connecting');
            isUsingHlsFallback = true;
            if (pc) {
                pc.close();
                pc = null;
            }
            if (video && video.srcObject) {
                video.srcObject = null;
            }
            if (hlsUrl) {
                const token = readToken || hlsUrl.split('token=')[1] || '';
                startHls(hlsUrl, token);
            } else {
                setError("Camera stream unavailable");
                setIsLoading(false);
                onStateChange?.('offline');
            }
        };

        const handleRetry = () => {
            if (!isMounted || isUsingHlsFallback) return;

            if (retries >= MAX_RETRIES) {
                startHlsFallback();
                return;
            }

            retries++;
            console.log(`[webrtc] Connection failed/disconnected. Retry ${retries}/${MAX_RETRIES} in 4s...`);
            setIsLoading(true);
            onStateChange?.('connecting');

            if (retryTimer) clearTimeout(retryTimer);
            retryTimer = setTimeout(async () => {
                if (!isMounted) return;
                if (retries % 2 === 0) {
                    const freshData = await refreshStream();
                    if (!isMounted) return;
                    if (freshData) {
                        streamId = freshData.stream_id;
                        readToken = freshData.read_token;
                        hlsUrl = freshData.hls_url;
                    }
                }
                connectWebRTC();
            }, 4000);
        };

        const connectWebRTC = async () => {
            if (!isMounted || isUsingHlsFallback) return;

            if (pc) {
                pc.close();
                pc = null;
            }

            video.srcObject = null;

            if (!streamId) {
                startHlsFallback();
                return;
            }

            try {
                pc = new RTCPeerConnection({
                    iceServers: [{ urls: 'stun:stun.l.google.com:19302' }]
                });

                pc.addTransceiver('video', { direction: 'recvonly' });
                pc.addTransceiver('audio', { direction: 'recvonly' });

                pc.ontrack = (event) => {
                    if (!isMounted) return;
                    if (!video.srcObject) {
                        video.srcObject = new MediaStream();
                    }
                    (video.srcObject as MediaStream).addTrack(event.track);
                    setError(null);
                };

                pc.oniceconnectionstatechange = () => {
                    if (!isMounted) return;
                    const state = pc?.iceConnectionState;
                    console.log(`[webrtc] ICE Connection State: ${state}`);
                    if (state === 'connected' || state === 'completed') {
                        retries = 0;
                        setError(null);
                        setIsLoading(false);
                        onStateChange?.('live');
                    } else if (state === 'failed' || state === 'disconnected') {
                        handleRetry();
                    }
                };

                const offer = await pc.createOffer();
                if (!isMounted) return;
                await pc.setLocalDescription(offer);

                // This waits for ALL ICE candidates before sending the offer
                await new Promise<void>((resolve) => {
                    if (!isMounted || pc?.iceGatheringState === 'complete') {
                        resolve();
                        return;
                    }
                    const checkGathering = () => {
                        if (!isMounted || pc?.iceGatheringState === 'complete') {
                            pc?.removeEventListener('icegatheringstatechange', checkGathering);
                            resolve();
                        }
                    };
                    pc?.addEventListener('icegatheringstatechange', checkGathering);
                    // Reduce to 1s — MediaMTX WHEP doesn't need full ICE gathering
                    setTimeout(() => {
                        pc?.removeEventListener('icegatheringstatechange', checkGathering);
                        resolve();
                    }, 1000);
                });

                if (!isMounted) return;

                const whepUrl = `https://${window.location.hostname}/stream/${streamId}/whep?token=${readToken}`;
                console.log(`[webrtc] WHEP handshake with: ${whepUrl}`);

                const response = await fetch(whepUrl, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/sdp' },
                    body: pc.localDescription?.sdp
                });

                if (!isMounted) return;

                if (!response.ok) {
                    throw new Error(`WHEP failed with status: ${response.status}`);
                }

                const answerSdp = await response.text();
                if (!isMounted) return;

                await pc.setRemoteDescription(new RTCSessionDescription({
                    type: 'answer',
                    sdp: answerSdp
                }));

                console.log("[webrtc] WebRTC playback started successfully");
            } catch (err) {
                console.warn("[webrtc] Handshake failed, scheduling retry:", err);
                handleRetry();
            }
        };

        const initStream = async () => {
            setIsLoading(true);
            onStateChange?.('connecting');
            const data = await refreshStream();
            if (!isMounted) return;
            if (data) {
                streamId = data.stream_id;
                readToken = data.read_token;
                hlsUrl = data.hls_url;

                if (streamId) {
                    connectWebRTC();
                } else {
                    startHlsFallback();
                }
            } else {
                setError("Failed to get stream access");
                setIsLoading(false);
                onStateChange?.('offline');
            }
        };

        initStream();

        return () => {
            isMounted = false;
            if (retryTimer) clearTimeout(retryTimer);
            if (pc) {
                pc.close();
                pc = null;
            }
            if (hls) {
                hls.destroy();
                hls = null;
            }
        };
    }, [camera]);

    if (error) {
        return (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-black text-white p-4 z-30">
                <p className="text-red-500 text-sm mb-2">{error}</p>
                <button
                    onClick={() => window.location.reload()}
                    className="px-3 py-1 bg-gray-800 rounded text-xs hover:bg-gray-700"
                >
                    Retry
                </button>
            </div>
        );
    }

    return (
        <div className={`absolute inset-0 w-full h-full flex items-center justify-center bg-black overflow-hidden ${isSinglePlayer ? 'p-2' : ''}`}>
            {isLoading && !error && (
                <div className="absolute inset-0 z-20 flex items-center justify-center bg-black/40 backdrop-blur-[2px]">
                    <div className="relative">
                        <div className="w-12 h-12 border-4 border-[#422568]/10 border-t-[#422568] rounded-full animate-spin" />
                        <div className="absolute inset-0 flex items-center justify-center">
                            <LayoutDashboard className="w-4 h-4 text-[#422568] animate-pulse" />
                        </div>
                    </div>
                </div>
            )}
            <video
                ref={videoRef}
                className="w-full h-full object-contain"
                controls
                autoPlay
                muted
            />
        </div>
    );
};

export default function Cameras({ clientId }: { clientId?: number }) {
    const [cameras, setCameras] = useState<CameraType[]>([]);
    const [selectedCamera, setSelectedCamera] = useState<CameraType | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [newStream, setNewStream] = useState({ name: '', url: '', type: 'on_demand' });
    const [searchQuery, setSearchQuery] = useState('');
    const [isMultiviewOpen, setIsMultiviewOpen] = useState(false);
    const [multiviewLayout, setMultiviewLayout] = useState(1);
    const [selectedMultiviewCameras, setSelectedMultiviewCameras] = useState<(string | null)[]>([]);
    const [cameraThumbnails, setCameraThumbnails] = useState<Record<string, string>>({});
    const [streamStates, setStreamStates] = useState<Record<string, 'connecting' | 'live' | 'offline'>>({});

    useEffect(() => {
        const storedThumbnails = localStorage.getItem('cameraThumbnails');
        if (storedThumbnails) {
            setCameraThumbnails(JSON.parse(storedThumbnails));
        }
    }, []);

    useEffect(() => {
        if (Object.keys(cameraThumbnails).length === 0) return;

        let currentThumbnails = { ...cameraThumbnails };
        let success = false;

        while (!success && Object.keys(currentThumbnails).length > 0) {
            try {
                localStorage.setItem('cameraThumbnails', JSON.stringify(currentThumbnails));
                success = true;
            } catch (error) {
                console.warn('LocalStorage quota exceeded. Trimming oldest thumbnail...');
                const keys = Object.keys(currentThumbnails);
                if (keys.length > 0) {
                    delete currentThumbnails[keys[0]];
                }
            }
        }

        if (Object.keys(currentThumbnails).length !== Object.keys(cameraThumbnails).length) {
            setCameraThumbnails(currentThumbnails);
        }
    }, [cameraThumbnails]);

    useEffect(() => {
        setSelectedCamera(null);
        setMultiviewLayout(1);
        setSelectedMultiviewCameras([]);
        setStreamStates({});
        fetchCameras();
    }, []);

    const fetchCameras = async () => {
        try {
            const queryObj: any = {
                asset_type: 'camera',
                ...(clientId ? { client_id: clientId } : {})
            };

            const response = await baseapi.get('assets', {
                params: {
                    query: JSON.stringify(queryObj),
                    limit: 1000
                }
            });

            if (response.data.status) {
                const jwt = jwt_decode();
                const tenant_name = jwt.tenant_id || jwt.tenant_name;
                const org_id = jwt.organization_id;

                const mappedCameras = response.data.data.map((cam: any) => {
                    const streamPath = tenant_name
                        ? `${tenant_name}-${cam.organization_id || org_id}-${cam.client_id}-${cam.agent_id}_${cam.id}`
                        : `${cam.organization_id || org_id}-${cam.client_id}-${cam.agent_id}_${cam.id}`;

                    return {
                        id: String(cam.id),
                        name: cam.name,
                        status: cam.status === 'active' || cam.status === 'online' ? 'online' : 'offline',
                        url: `https://${window.location.hostname}/${streamPath}/`,
                        client_id: String(cam.client_id),
                        agent_id: String(cam.agent_id)
                    };
                });
                setCameras(mappedCameras);
            } else {
                showError("Failed to fetch cameras");
            }
        } catch (error) {
            console.error("Error fetching cameras:", error);
            showError("Error fetching cameras");
        }
    };

    const handleAddStream = () => {
        if (!newStream.name || !newStream.url) {
            showError("Please fill all fields");
            return;
        }

        const newCam = {
            id: `cam_0${cameras.length + 1}`,
            name: newStream.name,
            status: 'online',
            url: newStream.url
        };

        setCameras([...cameras, newCam]);
        setIsModalOpen(false);
        setNewStream({ name: '', url: '', type: 'on_demand' });
        showSuccess("Stream added successfully (Local)");
    };

    const toggleCameraSelection = (camId: string) => {
        if (selectedMultiviewCameras.includes(camId)) {
            setSelectedMultiviewCameras(prev => prev.map(id => id === camId ? null : id));
        } else {
            const emptyIndex = selectedMultiviewCameras.findIndex(id => id === null);
            if (emptyIndex !== -1) {
                setSelectedMultiviewCameras(prev => {
                    const next = [...prev];
                    next[emptyIndex] = camId;
                    return next;
                });
            } else {
                showError(`Multiview is full for ${Math.sqrt(multiviewLayout)}x${Math.sqrt(multiviewLayout)} layout. Remove a camera first.`);
            }
        }
    };

    const enterMultiview = (layout: number) => {
        setMultiviewLayout(layout);
        setIsMultiviewOpen(false);
        setSelectedCamera(null);
        setSelectedMultiviewCameras(Array(layout).fill(null));
    };

    return (
        <div className="bg-slate-50 flex-1 w-full text-slate-800 font-sans overflow-hidden flex flex-col">
            {selectedCamera || multiviewLayout > 1 ? (
                <div className="flex flex-row flex-1 bg-white overflow-hidden h-full">
                    {/* Left Sidebar Layout */}
                    <div className="w-full md:w-64 border-r border-slate-200 flex flex-col bg-slate-50 h-full overflow-hidden shrink-0">
                        <div className="p-4 flex flex-col h-full bg-slate-50 animate-in fade-in duration-500">
                            <div className="flex justify-between items-center mb-4">
                                <div className='flex gap-2 items-center'>
                                    <button
                                        onClick={() => {
                                            setSelectedCamera(null);
                                            setMultiviewLayout(1);
                                            setSelectedMultiviewCameras([]);
                                            setStreamStates({});
                                        }}
                                        className="text-xs text-white font-semibold bg-[#422568] px-2 py-2 rounded-lg border border-[#422568]"
                                    >
                                        <ArrowLeft className="w-4 h-4" />
                                    </button>
                                    <h3 className="text-sm font-bold text-slate-500 uppercase tracking-wider">All Cameras</h3>
                                </div>
                                {multiviewLayout > 1 && (
                                    <span className="text-xs text-white font-semibold bg-[#422568] px-2 py-0.5 rounded-full border border-[#422568]">
                                        {selectedMultiviewCameras.filter(Boolean).length}/{multiviewLayout}
                                    </span>
                                )}
                            </div>

                            <div className="relative mb-5">
                                <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                                <input
                                    type="text"
                                    placeholder="Search cameras..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="w-full bg-white border border-slate-300 rounded-lg pl-9 pr-4 py-2 text-xs text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#422568] focus:border-[#422568] transition-all shadow-sm"
                                />
                            </div>

                            <div className="space-y-1.5 overflow-y-auto custom-scroll flex-1 pr-1">
                                {cameras.filter(cam => cam.name.toLowerCase().includes(searchQuery.toLowerCase())).map((cam) => (
                                    <div
                                        key={cam.id}
                                        onClick={() => {
                                            if (multiviewLayout > 1) {
                                                toggleCameraSelection(cam.id);
                                            } else {
                                                setSelectedCamera(cam);
                                                setMultiviewLayout(1);
                                            }
                                        }}
                                        draggable={multiviewLayout > 1 && cam.status === 'online'}
                                        onDragStart={(e) => {
                                            e.dataTransfer.setData('cameraId', cam.id);
                                        }}
                                        className={`p-2.5 rounded-lg cursor-pointer transition-colors border flex items-center justify-between ${multiviewLayout > 1
                                            ? selectedMultiviewCameras.includes(cam.id)
                                                ? 'bg-[#422568]/20 border-[#422568] text-[#422568]'
                                                : 'bg-white border-slate-200 hover:border-slate-300 hover:bg-slate-100 text-slate-700'
                                            : (selectedCamera && selectedCamera.id === cam.id)
                                                ? 'bg-[#422568]/20 border-[#422568] text-[#422568]'
                                                : 'bg-white border-slate-200 hover:border-slate-300 hover:bg-slate-100 text-slate-700'
                                            }`}
                                    >
                                        <div className="flex items-center gap-2 truncate">
                                            <Video className={`w-3.5 h-3.5 shrink-0 ${multiviewLayout > 1 && selectedMultiviewCameras.includes(cam.id) || (selectedCamera && selectedCamera.id === cam.id) ? 'text-[#422568]' : 'text-slate-400'}`} />
                                            <span className="text-xs font-semibold truncate">{cam.name}</span>
                                        </div>
                                        <span className={`w-1.5 h-1.5 rounded-full shrink-0 shadow-sm ${cam.status === 'online' ? 'bg-emerald-500' : 'bg-red-500'}`}></span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    <div className="flex-1 flex flex-col bg-slate-100">
                        {/* Header Area in Multiview/Single View */}
                        {/* <div className="flex items-center justify-between bg-white px-4 py-3 border-b border-slate-200 min-h-[60px] shadow-sm z-10">
                            <div className="flex items-center min-w-0 flex-1">
                                <h1 className="text-lg md:text-xl font-extrabold text-slate-800 flex items-center gap-2 truncate leading-none">
                                    <Video className="text-[#422568] w-5 h-5 flex-shrink-0" />
                                    <span className="truncate">
                                        {multiviewLayout > 1
                                            ? `Multiview ${Math.sqrt(multiviewLayout)}x${Math.sqrt(multiviewLayout)}`
                                            : 'Camera Monitoring'}
                                    </span>
                                </h1>
                            </div>

                            <button
                                onClick={() => {
                                    setSelectedCamera(null);
                                    setMultiviewLayout(1);
                                    setSelectedMultiviewCameras([]);
                                    setStreamStates({});
                                }}
                                className="ml-3 flex items-center gap-2 flex-shrink-0 text-slate-600 hover:text-slate-900 bg-white hover:bg-slate-50 px-3 py-2 rounded-lg transition-colors text-sm font-semibold border border-slate-300 shadow-sm whitespace-nowrap"
                            >
                                <ArrowLeft className="w-4 h-4 flex-shrink-0" />
                                <span>Back to Cameras</span>
                            </button>
                        </div> */}

                        {/* Player Area */}
                        <div className="flex-1 flex flex-col min-h-0 overflow-y-auto custom-scroll p-2 pb-28">
                            {multiviewLayout === 16 ? (
                                <div className="grid grid-cols-2 grid-rows-2 gap-3 flex-1 min-h-0">
                                    {[0, 1, 2, 3].map(groupIndex => (
                                        <div key={groupIndex} className="bg-white rounded-xl overflow-hidden border border-slate-300 flex flex-col shadow-sm p-1.5 h-full w-full min-h-0">
                                            <div className="grid grid-cols-2 grid-rows-2 gap-1.5 flex-1 h-full min-h-0">
                                                {[0, 1, 2, 3].map(subIndex => {
                                                    const actualIndex = groupIndex * 4 + subIndex;
                                                    const camId = selectedMultiviewCameras[actualIndex];
                                                    const cam = cameras.find(c => c.id === camId);

                                                    return (
                                                        <div
                                                            key={subIndex}
                                                            className="relative bg-black rounded-lg overflow-hidden flex flex-col items-center justify-center group h-full w-full min-h-0 shadow-inner"
                                                            onDragOver={(e) => e.preventDefault()}
                                                            onDrop={(e) => {
                                                                e.preventDefault();
                                                                const droppedCamId = e.dataTransfer.getData('cameraId');
                                                                if (droppedCamId) {
                                                                    setSelectedMultiviewCameras(prev => {
                                                                        const next = [...prev];
                                                                        next[actualIndex] = droppedCamId;
                                                                        return next;
                                                                    });
                                                                }
                                                            }}
                                                        >
                                                            {cam ? (
                                                                <>
                                                                    {cam.status === 'online' ? (
                                                                        <VideoPlayer
                                                                            camera={cam}
                                                                            onStateChange={(state) => setStreamStates(prev => ({ ...prev, [cam.id]: state }))}
                                                                        />
                                                                    ) : (
                                                                        <div className="text-center">
                                                                            <Video className="w-6 h-6 text-red-500/80 mx-auto mb-1" />
                                                                            <p className="text-slate-300 text-[10px]">Offline</p>
                                                                        </div>
                                                                    )}
                                                                    <div className="absolute top-2 left-2 right-2 flex justify-between items-center opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                                                                        <div className="bg-black/70 backdrop-blur-md px-2.5 py-1 rounded-full text-[10px] font-bold text-white border border-white/20 flex items-center gap-1.5 truncate max-w-[70%] shadow-lg">
                                                                            {(() => {
                                                                                const currentStatus = streamStates[cam.id] || 'connecting';
                                                                                const dotColor = currentStatus === 'live' ? 'bg-emerald-500' : currentStatus === 'connecting' ? 'bg-amber-500' : 'bg-red-500';
                                                                                return <span className={`w-1.5 h-1.5 rounded-full ${dotColor} shrink-0`}></span>;
                                                                            })()}
                                                                            <span className="truncate">{cam.name}</span>
                                                                        </div>
                                                                        <button
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                setSelectedMultiviewCameras(prev => {
                                                                                    const next = [...prev];
                                                                                    next[actualIndex] = null;
                                                                                    return next;
                                                                                });
                                                                            }}
                                                                            className="bg-black/70 backdrop-blur-md p-1 rounded-full text-slate-200 hover:text-red-400 border border-white/20 transition-colors shadow-lg"
                                                                        >
                                                                            <X className="w-3.5 h-3.5" />
                                                                        </button>
                                                                    </div>
                                                                </>
                                                            ) : (
                                                                <div className="flex flex-col items-center justify-center text-slate-500">
                                                                    <Plus className="w-5 h-5 mb-1 opacity-50" />
                                                                    <p className="text-[10px] uppercase font-bold tracking-wider opacity-50">Drop here</p>
                                                                </div>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : multiviewLayout > 1 ? (
                                <div
                                    className="grid flex-1 min-h-0 w-full"
                                    style={{
                                        gridTemplateColumns: `repeat(${Math.sqrt(multiviewLayout)}, minmax(0, 1fr))`,
                                        gridTemplateRows: `repeat(${Math.sqrt(multiviewLayout)}, minmax(0, 1fr))`,
                                        gap: multiviewLayout === 4 ? '0.75rem' : multiviewLayout === 9 ? '0.5rem' : '0.375rem'
                                    }}
                                >
                                    {Array.from({ length: multiviewLayout }).map((_, index) => {
                                        const camId = selectedMultiviewCameras[index];
                                        const cam = cameras.find(c => c.id === camId);
                                        const isHighDensity = multiviewLayout >= 9;

                                        return (
                                            <div
                                                key={index}
                                                className="bg-white rounded-xl overflow-hidden border border-slate-300 flex flex-col shadow-sm h-full w-full min-h-0"
                                                onDragOver={(e) => {
                                                    e.preventDefault();
                                                }}
                                                onDrop={(e) => {
                                                    e.preventDefault();
                                                    const droppedCamId = e.dataTransfer.getData('cameraId');
                                                    if (droppedCamId) {
                                                        setSelectedMultiviewCameras(prev => {
                                                            const next = [...prev];
                                                            next[index] = droppedCamId;
                                                            return next;
                                                        });
                                                    }
                                                }}
                                            >
                                                {cam ? (
                                                    <div className="relative flex-1 bg-black flex flex-col items-center justify-center group h-full w-full min-h-0 shadow-inner">
                                                        {isHighDensity ? (
                                                            <>
                                                                <div className="absolute top-2 left-2 bg-black/75 backdrop-blur-md px-2.5 py-1 rounded-full text-[10px] font-bold text-white border border-white/20 flex items-center gap-1.5 truncate max-w-[75%] opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-10 shadow-lg">
                                                                    {(() => {
                                                                        const currentStatus = streamStates[cam.id] || 'connecting';
                                                                        const dotColor = currentStatus === 'live' ? 'bg-emerald-500' : currentStatus === 'connecting' ? 'bg-amber-500' : 'bg-red-500';
                                                                        return <span className={`w-1.5 h-1.5 rounded-full ${dotColor} shrink-0`}></span>;
                                                                    })()}
                                                                    <span className="truncate">{cam.name}</span>
                                                                </div>
                                                                <button
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        setSelectedMultiviewCameras(prev => {
                                                                            const next = [...prev];
                                                                            next[index] = null;
                                                                            return next;
                                                                        });
                                                                    }}
                                                                    className="absolute top-2 right-2 bg-black/75 backdrop-blur-md p-1.5 rounded-full text-slate-200 hover:text-red-400 border border-white/20 opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-10 shadow-lg hover:scale-105"
                                                                >
                                                                    <X className="w-3.5 h-3.5" />
                                                                </button>
                                                            </>
                                                        ) : (
                                                            <div className="absolute top-0 left-0 right-0 p-3 bg-gradient-to-b from-black/80 to-transparent flex justify-between items-center z-10">
                                                                <div className="truncate mr-2">
                                                                    <h2 className="text-sm font-bold text-white truncate drop-shadow-md">{cam.name}</h2>
                                                                </div>
                                                                <div className="flex items-center gap-2 shrink-0">
                                                                    {(() => {
                                                                        const currentStatus = streamStates[cam.id] || 'connecting';
                                                                        const dotColor = currentStatus === 'live' ? 'bg-emerald-500' : currentStatus === 'connecting' ? 'bg-amber-500' : 'bg-red-500';
                                                                        return (
                                                                            <>
                                                                                <span className={`w-1.5 h-1.5 rounded-full ${dotColor} animate-pulse shadow-sm`}></span>
                                                                                <span className="text-[10px] font-bold uppercase tracking-wider text-slate-200 drop-shadow-md">
                                                                                    {currentStatus === 'live' ? 'Live' : currentStatus === 'connecting' ? '' : 'Offline'}
                                                                                </span>
                                                                            </>
                                                                        );
                                                                    })()}
                                                                    <button
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            setSelectedMultiviewCameras(prev => {
                                                                                const next = [...prev];
                                                                                next[index] = null;
                                                                                return next;
                                                                            });
                                                                        }}
                                                                        className="text-slate-300 hover:text-red-400 transition-colors bg-black/40 rounded-full p-1 border border-white/10"
                                                                    >
                                                                        <X className="w-4 h-4" />
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        )}

                                                        {cam.status === 'online' ? (
                                                            <VideoPlayer
                                                                camera={cam}
                                                                onStateChange={(state) => setStreamStates(prev => ({ ...prev, [cam.id]: state }))}
                                                            />
                                                        ) : (
                                                            <div className="text-center">
                                                                <Video className={`${multiviewLayout === 25 ? 'w-6 h-6' : 'w-8 h-8'} text-red-500/80 mx-auto mb-1.5`} />
                                                                <p className="text-slate-300 text-xs font-semibold">Offline</p>
                                                            </div>
                                                        )}
                                                    </div>
                                                ) : (
                                                    <div className="relative flex-1 bg-slate-50 flex flex-col items-center justify-center border-2 border-dashed border-slate-300 rounded-lg m-1 hover:border-[#422568] hover:bg-[#422568]/50 transition-all duration-300 group">
                                                        <Plus className={`${multiviewLayout === 25 ? 'w-4 h-4 mb-1' : 'w-6 h-6 mb-2'} text-slate-400 group-hover:text-[#422568] group-hover:scale-110 transition-all duration-300`} />
                                                        <p className={`${multiviewLayout === 25 ? 'text-[10px]' : 'text-xs'} font-bold uppercase tracking-wider text-slate-400 group-hover:text-[#422568] transition-colors`}>Drop here</p>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            ) : (
                                selectedCamera && (
                                    <div className="w-full mx-auto bg-white rounded-xl overflow-hidden border border-slate-300 flex flex-col shadow-md">
                                        <div className="p-4 border-b border-slate-200 flex justify-between items-center bg-slate-50">
                                            <div className="truncate mr-4">
                                                <h2 className="text-base font-extrabold text-slate-800 truncate">{selectedCamera.name}</h2>
                                            </div>
                                            <div className="flex items-center gap-2 shrink-0 bg-white border border-slate-200 px-3 py-1.5 rounded-full shadow-sm">
                                                {(() => {
                                                    const currentStatus = streamStates[selectedCamera.id] || 'connecting';
                                                    const dotColor = currentStatus === 'live' ? 'bg-emerald-500' : currentStatus === 'connecting' ? 'bg-amber-500' : 'bg-red-500';
                                                    return (
                                                        <>
                                                            <span className={`w-2 h-2 rounded-full ${dotColor} animate-pulse`}></span>
                                                            <span className="text-xs font-bold uppercase tracking-wider text-slate-600">
                                                                {currentStatus === 'live' ? 'Live' : currentStatus === 'connecting' ? '' : 'Offline'}
                                                            </span>
                                                        </>
                                                    );
                                                })()}
                                            </div>
                                        </div>

                                        <div className="relative aspect-video bg-black flex flex-col items-center justify-center group shadow-inner">
                                            {selectedCamera.status === 'online' ? (
                                                <VideoPlayer
                                                    camera={selectedCamera}
                                                    onStateChange={(state) => setStreamStates(prev => ({ ...prev, [selectedCamera.id]: state }))}
                                                    isSinglePlayer={true}
                                                />
                                            ) : (
                                                <div className="text-center">
                                                    <Video className="w-12 h-12 text-red-500/80 mx-auto mb-4" />
                                                    <p className="text-slate-300 text-sm font-semibold">Camera is offline</p>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )
                            )}
                        </div>
                    </div>
                </div>
            ) : (
                <div className="h-full overflow-y-auto custom-scroll p-2 md:p-2">
                    <div className="mx-auto">
                        {/* Header for Dashboard Main View */}
                        <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4 mb-2">
                            <div className="flex items-center gap-3">
                                <div className='bg-[#422568]/20 p-2 rounded-xl border border-[#422568]/20 shrink-0 shadow-sm'>
                                    <Video className='text-[#422568] w-4 h-4' />
                                </div>
                                <div>
                                    <h1 className="text-xl font-extrabold text-slate-800 tracking-tight">Monitoring System</h1>
                                </div>
                            </div>

                            <div className="flex flex-wrap items-center gap-3">
                                <div className="relative">
                                    <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                                    <input
                                        type="text"
                                        placeholder="Search cameras..."
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        className="bg-white border border-slate-300 rounded-lg pl-9 pr-4 py-1 text-sm text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#422568] focus:border-[#422568] transition-all w-full md:w-48 focus:md:w-64 shadow-sm"
                                    />
                                </div>

                                <div className="bg-[#422568]/20 border border-[#422568]/20 px-4 py-1 rounded-lg shadow-sm hidden sm:block">
                                    <span className="text-sm text-[#422568] font-bold">{cameras.filter((c) => c.status === 'online').length} Online</span>
                                </div>

                                <div className="relative">
                                    <button
                                        onClick={() => setIsMultiviewOpen(!isMultiviewOpen)}
                                        className="bg-white hover:bg-slate-50 text-slate-700 px-4 py-1 rounded-lg text-sm font-bold transition-colors flex items-center gap-2 border border-slate-300 shadow-sm"
                                    >
                                        <Grid className="w-4 h-4" />
                                        Multiview
                                    </button>
                                    {isMultiviewOpen && (
                                        <div className="absolute right-0 mt-2 w-48 bg-white border border-slate-200 rounded-xl shadow-xl z-20 overflow-hidden py-1">
                                            <button onClick={() => { setMultiviewLayout(1); setIsMultiviewOpen(false); setSelectedCamera(cameras[0]); }} className="w-full text-left px-4 py-2.5 text-sm font-medium text-slate-600 hover:bg-slate-50 hover:text-[#422568] flex items-center gap-3 transition-colors">
                                                <Square className="w-4 h-4" /> 1x1 View
                                            </button>
                                            <button onClick={() => enterMultiview(4)} className="w-full text-left px-4 py-2.5 text-sm font-medium text-slate-600 hover:bg-slate-50 hover:text-[#422568] flex items-center gap-3 transition-colors">
                                                <Grid className="w-4 h-4" /> 2x2 View
                                            </button>
                                            {/* <button onClick={() => enterMultiview(9)} className="w-full text-left px-4 py-2.5 text-sm font-medium text-slate-600 hover:bg-slate-50 hover:text-[#422568] flex items-center gap-3 transition-colors">
                                            <Grid className="w-4 h-4" /> 3x3 View
                                        </button>
                                        <button onClick={() => enterMultiview(16)} className="w-full text-left px-4 py-2.5 text-sm font-medium text-slate-600 hover:bg-slate-50 hover:text-[#422568] flex items-center gap-3 transition-colors">
                                            <Grid className="w-4 h-4" /> 4x4 View
                                        </button>
                                        <button onClick={() => enterMultiview(25)} className="w-full text-left px-4 py-2.5 text-sm font-medium text-slate-600 hover:bg-slate-50 hover:text-[#422568] flex items-center gap-3 transition-colors">
                                            <Grid className="w-4 h-4" /> 5x5 View
                                        </button> */}
                                        </div>
                                    )}
                                </div>

                                <button
                                    onClick={() => setIsModalOpen(true)}
                                    className="bg-[#422568] hover:bg-[#422568] text-white px-4 py-1 rounded-lg text-sm font-bold transition-colors flex items-center gap-2 shadow-md shadow-[#422568]/20"
                                >
                                    <Plus className="w-4 h-4" />
                                    Add Stream
                                </button>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-4 pb-20">
                            {cameras.filter(cam => cam.name.toLowerCase().includes(searchQuery.toLowerCase())).map((camera) => (
                                <div
                                    key={camera.id}
                                    className="bg-white border border-slate-200 rounded-xl overflow-hidden hover:border-[#422568] transition-all duration-300 hover:shadow-lg hover:shadow-[#422568]/10 group flex flex-col h-full"
                                >
                                    <div className="relative aspect-video bg-slate-900 flex items-center justify-center group-hover:bg-slate-800 transition-colors">
                                        {cameraThumbnails[camera.id] ? (
                                            <img src={cameraThumbnails[camera.id]} className="w-full h-full object-cover opacity-90 group-hover:opacity-100 transition-opacity" />
                                        ) : (
                                            <Video className={`w-12 h-12 ${camera.status === 'online' ? 'text-slate-600 group-hover:text-[#422568]' : 'text-slate-700'} transition-colors duration-300`} />
                                        )}
                                        <div className="absolute top-3 left-3 flex items-center gap-1.5 bg-white/90 backdrop-blur-sm px-2.5 py-1 rounded-full border border-slate-200 shadow-sm">
                                            <span className={`w-2 h-2 rounded-full ${camera.status === 'online' ? 'bg-emerald-500' : 'bg-red-500'} animate-pulse`}></span>
                                            <span className="text-[10px] font-extrabold text-slate-700 uppercase tracking-wider">{camera.status}</span>
                                        </div>
                                    </div>

                                    <div className="p-4 flex flex-col flex-1 justify-between bg-white">
                                        <div className="mb-4">
                                            <h3 className="text-base font-extrabold text-slate-800 group-hover:text-[#422568] transition-colors line-clamp-1">{camera.name}</h3>
                                            <p className="text-xs font-medium text-slate-500 mt-1">RTSP Stream Available</p>
                                        </div>

                                        <button
                                            onClick={() => { setSelectedCamera(camera); setMultiviewLayout(1); }}
                                            disabled={camera.status !== 'online'}
                                            className={`w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-bold transition-all duration-200
                                                        ${camera.status === 'online'
                                                    ? 'bg-[#422568]/30 hover:bg-[#422568]/20 text-[#422568] hover:text-[#422568] border border-[#422568]/10 hover:border-[#422568]/10 shadow-sm'
                                                    : 'bg-slate-100 text-slate-400 cursor-not-allowed border border-slate-200'
                                                }`}
                                        >
                                            <Play className="w-4 h-4" />
                                            <span>Stream</span>
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* Modal */}
            {isModalOpen && (
                <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                    <div className="bg-white border border-slate-200 rounded-2xl w-full max-w-md p-6 shadow-2xl">
                        <div className="flex justify-between items-center mb-6">
                            <h2 className="text-xl font-extrabold text-slate-800">Add New Stream</h2>
                            <button onClick={() => setIsModalOpen(false)} className="text-slate-400 hover:text-slate-600 bg-slate-100 hover:bg-slate-200 p-1.5 rounded-full transition-colors">
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-bold text-slate-700 mb-1.5">Stream Name</label>
                                <input
                                    type="text"
                                    value={newStream.name}
                                    onChange={(e) => setNewStream({ ...newStream, name: e.target.value })}
                                    className="w-full bg-slate-50 border border-slate-300 rounded-lg px-3 py-2 text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#422568] focus:border-[#422568] shadow-sm"
                                    placeholder="e.g. Front Entrance"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-bold text-slate-700 mb-1.5">Stream URL</label>
                                <input
                                    type="text"
                                    value={newStream.url}
                                    onChange={(e) => setNewStream({ ...newStream, url: e.target.value })}
                                    className="w-full bg-slate-50 border border-slate-300 rounded-lg px-3 py-2 text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#422568] focus:border-[#422568] shadow-sm"
                                    placeholder="rtsp://..."
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-bold text-slate-700 mb-1.5">Stream Type</label>
                                <select
                                    value={newStream.type}
                                    onChange={(e) => setNewStream({ ...newStream, type: e.target.value })}
                                    className="w-full bg-slate-50 border border-slate-300 rounded-lg px-3 py-2 text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#422568] focus:border-[#422568] shadow-sm"
                                >
                                    <option value="on_demand">On Demand</option>
                                    <option value="persistent">Persistent Connection</option>
                                </select>
                            </div>
                        </div>

                        <div className="flex justify-end gap-3 mt-8">
                            <button
                                onClick={() => setIsModalOpen(false)}
                                className="px-5 py-2.5 bg-white hover:bg-slate-50 border border-slate-300 text-slate-700 rounded-lg text-sm font-bold transition-colors shadow-sm"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleAddStream}
                                className="px-5 py-2.5 bg-[#422568] hover:bg-[#422568] text-white rounded-lg text-sm font-bold transition-colors shadow-md shadow-[#422568]/20"
                            >
                                Add Stream
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}